import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import * as Tone from 'tone';
import { Meter } from 'tone'; // Explicit import for clarity
import { observer, inject } from "mobx-react";
import { Popover, Button, IconButton, Slider, Typography, Box, TextField, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Select, MenuItem, Switch, FormControlLabel, Tooltip, Stack } from '@mui/material';
import { styled } from "@mui/material/styles";
import { debounce } from 'lodash';
import { titleize } from "../utils/stringUtils"
import Sequence from '../models/sequence'
import axios from 'axios';

const MAX_STEPS = 128;
const MAX_SAMPLES = 12;
const DEFAULT_TRACK_LENGTH = 16;

const SFIconButton = styled(IconButton)`
  width: 40px;
  height: 40px;
  padding: 20px;
  font-size: 1rem;
  &active, &:focus, &.Mui-focusVisible {
    background-color: white;
  }
`;

const SFPlayButton = styled(Button, {
  shouldForwardProp: (prop) => prop !== 'isPlaying',
})(({ theme, isPlaying }) => ({
  '&active, &:focus, &.Mui-focusVisible': {
    backgroundColor: isPlaying ? theme.palette.alert.main : theme.palette.playing.main,
  },
}));


const SFClearButton = styled(Button)(({ theme }) => ({
  '&:active, &:focus, &.Mui-focusVisible': {
    backgroundColor: `white !important`,
  },
}));

const SFSaveButton = styled(Button)(({ theme }) => ({
  '&:active, &:focus, &.Mui-focusVisible': {
    backgroundColor: `${theme.palette.primary.dark}!important`,
    color: `${theme.palette.primary.contrastText}!important`,
  },
}));

const Cell = styled('div')(
  ({ isActive, isPlaying, isDisabled, isWithinTrackLength }) => ({
    borderRadius: '3px',
    width: '20px',
    height: '20px',
    border: '1px solid',
    borderColor: isPlaying ? '#E2264D' : '#9ca3af',
    backgroundColor: isActive ? '#64b5f6' : '#e5e7eb',
    cursor: isDisabled || !isWithinTrackLength ? 'default' : 'pointer',
    opacity: isWithinTrackLength ? 1 : 0.3,
    transition: 'transform 0.1s, background-color 0.1s',
    position: 'relative',
    zIndex: 1,
    '&:hover': {
      transform:
        isDisabled || !isWithinTrackLength ? 'none' : 'scale(1.1)',
      backgroundColor: isActive ? '#42a5f5' : '#d4d7db',
    },
  })
);

const GridContainer = styled('div')({
  width: 'fit-content',
  maxWidth: 'calc(32 * 24px)',
  overflowX: 'auto',
  scrollBehavior: 'smooth',
  margin: '0 5px',
  pointerEvents: 'all',
  '&::-webkit-scrollbar': {
    height: '10px',
  },
  '&::-webkit-scrollbar-track': {
    background: '#f1f1f1',
    borderRadius: '5px',
  },
  '&::-webkit-scrollbar-thumb': {
    background: '#888',
    borderRadius: '5px',
    '&:hover': {
      background: '#555',
    },
  },
});

  // Styled Switch to override conflicting global checkbox styles from MaterializeCSS
  const StyledSwitch = styled(Switch)(({ theme }) => ({
    // Target the thumb specifically
    '&.MuiSwitch-root .MuiSwitch-thumb': {
      // Override conflicting properties with values closer to MUI defaults + !important
      position: 'initial !important',    // Override external 'relative'
      paddingLeft: '0 !important',    // Override external '35px'
      cursor: 'pointer !important',   // Keep default
      display: 'inline-block !important', // Keep default
      height: '20px !important',      // Override external '25px' with common MUI value
      lineHeight: 'normal !important',// Override external '25px'
      fontSize: 'initial !important', // Override external '1rem'
      userSelect: 'none !important',  // Keep default

      // Keep pseudo-element resets
      '&::before, &::after': {
        content: '""',
        border: 'none !important',
        position: 'static !important',
        display: 'none !important',
        width: 'auto !important',
        height: 'auto !important',
        margin: '0 !important',
        padding: '0 !important',
        zIndex: 'auto !important',
        transition: 'none !important',
      },
    },
  }));

// Define the component separately
const Sequencer = (props) => {
  const { sampleStore, userStore, snackbarStore } = props;

  // Refs for Tone.js objects to avoid re-instantiating
  const playersRef = useRef(null);
  const sequencerRef = useRef(null);
  const reverbRef = useRef(null);
  const chunksRef = useRef([]);
  const currentSequenceRef = useRef(null);
  const gridContainerRef = useRef(null);
  const metersRef = useRef({}); // Ref for Tone.Meter instances
  const volumeMeterDivsRef = useRef({}); // Ref for the inner divs of VolumeMeter components

  // State declarations - add sequenceName near the top with other useState calls
  const [isInitialized, setIsInitialized] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [sequenceName, setSequenceName] = useState(() => {
    return localStorage.getItem('sequenceName') || '';
  });
  const [tempo, setTempo] = useState(() => {
    return parseInt(localStorage.getItem('sequencerTempo')) || 120;
  });
  const [patterns, setPatterns] = useState(() => {
    return JSON.parse(localStorage.getItem('sequencerPatterns')) || {
      A: {
        sequenceData: {},
        trackLengths: {},
        playbackRates: {},
        volumeLevels: {},
        maxPlayDurations: {},
      }
    };
  });
  const [currentPattern, setCurrentPattern] = useState(() => {
    return localStorage.getItem('currentPattern') || 'A';
  });
  const [songSequence, setSongSequence] = useState(() => {
    return JSON.parse(localStorage.getItem('songSequence')) || ['A'];
  });
  const [isSongMode, setIsSongMode] = useState(false);
  const [currentSongStep, setCurrentSongStep] = useState(0);
  const [showPatternManager, setShowPatternManager] = useState(false);
  const [newPatternName, setNewPatternName] = useState('');
  const [isLoadingNewSamples, setIsLoadingNewSamples] = useState(false);

  const [trackLengths, setTrackLengths] = useState(() => {
    const patternId = localStorage.getItem('currentPattern') || 'A';
    const patterns = JSON.parse(localStorage.getItem('sequencerPatterns')) || {
      A: { trackLengths: {} }
    };
    return patterns[patternId]?.trackLengths || {};
  });

  const [playbackRates, setPlaybackRates] = useState(() => {
    const patternId = localStorage.getItem('currentPattern') || 'A';
    const patterns = JSON.parse(localStorage.getItem('sequencerPatterns')) || {
      A: { playbackRates: {} }
    };
    return patterns[patternId]?.playbackRates || {};
  });

  const [volumeLevels, setVolumeLevels] = useState(() => {
    const patternId = localStorage.getItem('currentPattern') || 'A';
    const patterns = JSON.parse(localStorage.getItem('sequencerPatterns')) || {
      A: { volumeLevels: {} }
    };
    return patterns[patternId]?.volumeLevels || {};
  });

  const [maxPlayDurations, setMaxPlayDurations] = useState(() => {
    const patternId = localStorage.getItem('currentPattern') || 'A';
    const patterns = JSON.parse(localStorage.getItem('sequencerPatterns')) || {
      A: { maxPlayDurations: {} }
    };
    return patterns[patternId]?.maxPlayDurations || {};
  });

  const [reverbMix, setReverbMix] = useState(() => {
    return parseFloat(localStorage.getItem('reverbMix')) || 0;
  });

  const [humanizationAmount, setHumanizationAmount] = useState(() => {
    return parseFloat(localStorage.getItem('humanizationAmount')) || 0;
  });

  const [swingAmount, setSwingAmount] = useState(() => {
    return parseFloat(localStorage.getItem('swingAmount')) || 0;
  });

  const [sampleKeys, setSampleKeys] = useState(() => {
    return JSON.parse(localStorage.getItem('sampleKeys')) || {};
  });

  const [trackPositions, setTrackPositions] = useState({});
  const [reverbPopoverAnchor, setReverbPopoverAnchor] = useState(null);
  const [sampleControlsPopoverAnchor, setSampleControlsPopoverAnchor] = useState(null);
  const [currentSampleId, setCurrentSampleId] = useState(null);
  const [showClearConfirmation, setShowClearConfirmation] = useState(false);

  const sampleSlugs = useRef(new Set());

  const [playersLoaded, setPlayersLoaded] = useState(false);

  const [isRecording, setIsRecording] = useState(false);
  const [recorder, setRecorder] = useState(null);

  // Add this state near the top with other state declarations
  const [recordingPhase, setRecordingPhase] = useState("idle");

  // Add refs for song mode
  const songModeRef = useRef(false);
  const songSequenceRef = useRef(['A']);
  const currentSongStepRef = useRef(0);

  // Fixed state initialization for sequenceData to prevent reference error
  const [sequenceData, setSequenceData] = useState(() => {
    const patternId = localStorage.getItem('currentPattern') || 'A';
    const patterns = JSON.parse(localStorage.getItem('sequencerPatterns')) || {
      A: { sequenceData: {} }
    };
    return patterns[patternId]?.sequenceData || {};
  });

  // Add function to handle auto-scrolling
  const scrollToStep = useCallback((step) => {
    if (!gridContainerRef.current) return;

    const container = gridContainerRef.current;
    const cellWidth = 24; // 20px cell + 4px gap
    const containerWidth = container.clientWidth;
    const scrollBuffer = 4; // Only scroll if the step is this many steps away from the edge

    // Calculate the left position of the current step
    const stepLeftPosition = step * cellWidth;
    const stepRightPosition = stepLeftPosition + cellWidth; // Position of the right edge of the step's cell

    const visibleLeftEdge = container.scrollLeft;
    const visibleRightEdge = visibleLeftEdge + containerWidth;

    // Define the target visible area considering the buffer
    const targetVisibleLeft = visibleLeftEdge + (scrollBuffer * cellWidth);
    const targetVisibleRight = visibleRightEdge - (scrollBuffer * cellWidth);

    let newScrollLeft = container.scrollLeft; // Start with current position

    // Need to scroll right? (Current step is beyond the right buffer zone)
    if (stepRightPosition > targetVisibleRight) {
      // Calculate position to place the step 'scrollBuffer' steps from the right edge
      newScrollLeft = stepRightPosition - containerWidth + (scrollBuffer * cellWidth);
    }
    // Need to scroll left? (Current step is before the left buffer zone)
    else if (stepLeftPosition < targetVisibleLeft) {
      // Calculate position to place the step 'scrollBuffer' steps from the left edge
      newScrollLeft = stepLeftPosition - (scrollBuffer * cellWidth);
    }

    // Ensure scroll position is within bounds [0, maxScroll]
    newScrollLeft = Math.max(0, newScrollLeft);
    newScrollLeft = Math.min(newScrollLeft, container.scrollWidth - containerWidth);

    // Only scroll if the calculated position is different from the current one
    if (Math.abs(newScrollLeft - container.scrollLeft) > 1) { // Use a small tolerance
        // console.log(`Scrolling to: ${newScrollLeft} for step ${step}`); // Optional debug log
        container.scrollTo({
            left: newScrollLeft,
            // Change back to smooth
            behavior: 'smooth'
        });
    }
  }, []); // Remove throttle, keep empty dependencies

  // Debounced save functions
  const saveSequenceData = debounce((data) => {
    // Update current pattern
    localStorage.setItem('sequencerPatterns', JSON.stringify({
      ...patterns,
      [currentPattern]: {
        ...patterns[currentPattern],
        sequenceData: data
      }
    }));

    setPatterns(prevPatterns => ({
      ...prevPatterns,
      [currentPattern]: {
        ...prevPatterns[currentPattern],
        sequenceData: data
      }
    }));
  }, 1000);

  const saveTrackLengths = debounce((data) => {
    // Update current pattern
    localStorage.setItem('sequencerPatterns', JSON.stringify({
      ...patterns,
      [currentPattern]: {
        ...patterns[currentPattern],
        trackLengths: data
      }
    }));

    setPatterns(prevPatterns => ({
      ...prevPatterns,
      [currentPattern]: {
        ...prevPatterns[currentPattern],
        trackLengths: data
      }
    }));
  }, 1000);

  const savePlaybackRates = debounce((data) => {
    // Update current pattern
    localStorage.setItem('sequencerPatterns', JSON.stringify({
      ...patterns,
      [currentPattern]: {
        ...patterns[currentPattern],
        playbackRates: data
      }
    }));

    setPatterns(prevPatterns => ({
      ...prevPatterns,
      [currentPattern]: {
        ...prevPatterns[currentPattern],
        playbackRates: data
      }
    }));
  }, 1000);

  const saveVolumeLevels = debounce((data) => {
    // Update current pattern
    localStorage.setItem('sequencerPatterns', JSON.stringify({
      ...patterns,
      [currentPattern]: {
        ...patterns[currentPattern],
        volumeLevels: data
      }
    }));

    setPatterns(prevPatterns => ({
      ...prevPatterns,
      [currentPattern]: {
        ...prevPatterns[currentPattern],
        volumeLevels: data
      }
    }));
  }, 1000);

  const saveMaxPlayDurations = debounce((data) => {
    // Update current pattern
    localStorage.setItem('sequencerPatterns', JSON.stringify({
      ...patterns,
      [currentPattern]: {
        ...patterns[currentPattern],
        maxPlayDurations: data
      }
    }));

    setPatterns(prevPatterns => ({
      ...prevPatterns,
      [currentPattern]: {
        ...prevPatterns[currentPattern],
        maxPlayDurations: data
      }
    }));
  }, 1000);

  // Add a new debounced save function for tempo
  const saveTempo = debounce((value) => {
    localStorage.setItem('sequencerTempo', value);
  }, 1000);

  // Add new debounced save functions
  const saveHumanizationAmount = debounce((value) => {
    localStorage.setItem('humanizationAmount', value);
  }, 1000);

  const saveSwingAmount = debounce((value) => {
    localStorage.setItem('swingAmount', value);
  }, 1000);

  // Add a new debounced save function for sample keys
  const saveSampleKeys = debounce((data) => {
    localStorage.setItem('sampleKeys', JSON.stringify(data));
  }, 1000);

  // Add new debounced save function for sequence name
  const saveSequenceName = debounce((name) => {
    localStorage.setItem('sequenceName', name);
  }, 1000);

  // Add new debounced save functions for patterns
  const savePatterns = debounce((data) => {
    localStorage.setItem('sequencerPatterns', JSON.stringify(data));
  }, 1000);

  const saveCurrentPattern = debounce((pattern) => {
    localStorage.setItem('currentPattern', pattern);
  }, 1000);

  const saveSongSequence = debounce((sequence) => {
    localStorage.setItem('songSequence', JSON.stringify(sequence));
  }, 1000);

  const saveReverbMix = debounce((mix) => {
    localStorage.setItem('reverbMix', mix);
  }, 1000);

  // Stop all players function - define this before using it
  const stopAllPlayers = () => {
    if (playersRef.current) {
      playersRef.current._players.forEach(player => {
        if (player.state === 'started') {
          player.stop();
        }
      });
    }
  };

  // Initialize audio on user interaction
  const initAudio = useCallback(async () => {
    console.log('Initializing audio...');

    if (!reverbRef.current) {
      reverbRef.current = new Tone.Reverb({
        decay: 2.5,
        wet: reverbMix,
      }).toDestination();
    }

    if (!playersRef.current) {
      playersRef.current = new Tone.Players(
        {},
        () => { console.log("Players loaded"); },
        {
          crossOrigin: "anonymous",
          onerror: (e) => console.error("Player loading error:", e)
        }
      ).connect(reverbRef.current);
    }

    Tone.Transport.bpm.value = tempo;

    setIsInitialized(true);
    console.log('Audio initialized. isInitialized:', true);
  }, [reverbMix, tempo]);

  // Update players on user interaction
  const updatePlayers = useCallback(async () => {
    try {
      console.log('Starting updatePlayers');
      setPlayersLoaded(false);

      await Tone.start();
      await Tone.loaded();

      // Make sure playersRef.current exists
      if (!playersRef.current) {
        playersRef.current = new Tone.Players(
          {},
          () => { console.log("Players loaded"); },
          {
            crossOrigin: "anonymous",
            onerror: (e) => console.error("Player loading error:", e)
          }
        )// Don't connect directly to reverb here
      }

      // Ensure reverbRef is initialized
      if (!reverbRef.current) {
          reverbRef.current = new Tone.Reverb({
            decay: 2.5,
            wet: reverbMix,
          }).toDestination();
      }

      const samples = sampleStore.selectedSamplesForSequencer.slice(0, MAX_SAMPLES);
      const sampleUrls = {};
      const currentSampleIds = new Set(); // Keep track of current samples

      // Add/update players and meters
      await Promise.all(
        samples.map(async (sample) => {
          const sampleId = sample.id.toString();
          currentSampleIds.add(sampleId); // Add to current set

          let player = playersRef.current.has(sampleId) ? playersRef.current.player(sampleId) : null;
          let meter = metersRef.current[sampleId];

          // Create meter if it doesn't exist
          if (!meter) {
            meter = new Meter();
            metersRef.current[sampleId] = meter;
            console.log(`Created meter for sample ${sampleId}`);
          }

          // Add player if it doesn't exist
          if (!player) {
             try {
               const url = new URL(sample.sample_mp3_url);
               url.searchParams.append('t', Date.now());
               await new Promise((resolve, reject) => {
                  const loadPlayer = (retryCount = 0) => {
                    playersRef.current.add(sampleId, url.toString(), () => {
                      console.log(`Successfully loaded sample: ${sampleId}`);
                      player = playersRef.current.player(sampleId);
                      // Connect player to meter, and meter to reverb
                      player.connect(meter);
                      meter.connect(reverbRef.current);
                      resolve();
                    }, (error) => {
                      console.error(`Error loading sample ${sampleId}:`, error);
                       if (retryCount < 2) {
                         console.log(`Retrying sample ${sampleId} (attempt ${retryCount + 1})...`);
                         setTimeout(() => loadPlayer(retryCount + 1), 500);
                       } else {
                         reject(error); // Reject after retries
                       }
                    });
                  }
                  loadPlayer();
               });
             } catch (error) {
               console.error(`Failed to add or load player ${sampleId}:`, error);
             }
          } else {
            // Ensure existing player/meter connections are correct
            // Disconnect first to avoid duplicate connections
            try { player.disconnect(meter); } catch (e) {}
            try { meter.disconnect(reverbRef.current); } catch (e) {}
            player.connect(meter);
            meter.connect(reverbRef.current);
          }
        })
      );

      // Remove unused players and meters
      Array.from(playersRef.current._players.keys()).forEach((sampleId) => {
        if (!currentSampleIds.has(sampleId)) {
          const player = playersRef.current.player(sampleId);
          const meter = metersRef.current[sampleId];

          // Dispose meter and remove from ref
          if (meter) {
            try {
              player?.disconnect(meter);
              meter.disconnect();
              meter.dispose();
              console.log(`Disposed meter for sample ${sampleId}`);
            } catch(e) { console.error(`Error disposing meter ${sampleId}:`, e); }
            delete metersRef.current[sampleId];
          }
          // Remove player
          playersRef.current.remove(sampleId);
           console.log(`Removed player ${sampleId}`);
        }
      });

      setPlayersLoaded(true);
      console.log('Players & Meters updated');
    } catch (error) {
      console.error('Error in updatePlayers:', error);
      setPlayersLoaded(false);
    }
  }, [sampleStore.selectedSamplesForSequencer, reverbMix]); // Added reverbMix dependency

  // Modify togglePlayback to handle song mode
  const togglePlayback = useCallback(async () => {
    if (!isInitialized) {
      await initAudio();
    }

    // Stop playback first if we're currently playing
    if (isPlaying) {
      Tone.Transport.stop();
      stopAllPlayers();
      setIsPlaying(false);
      return;
    }

    // Check if we have any new samples that need loading
    const currentSampleIds = new Set(Array.from(playersRef.current?._players?.keys() || []));
    const selectedSampleIds = new Set(sampleStore.selectedSamplesForSequencer.map(s => s.id.toString()));

    const hasNewSamples = sampleStore.selectedSamplesForSequencer.some(
      sample => !currentSampleIds.has(sample.id.toString())
    );

    if (hasNewSamples || !playersLoaded) {
      console.log('Loading new samples...');
      setIsLoadingNewSamples(true);
      try {
        await updatePlayers();
        // Verify all samples are loaded
        const allSamplesLoaded = sampleStore.selectedSamplesForSequencer.every(
          sample => playersRef.current?.has(sample.id.toString())
        );
        if (!allSamplesLoaded) {
          console.error('Not all samples loaded successfully');
          setIsLoadingNewSamples(false);
          return;
        }
      } catch (error) {
        console.error('Error loading samples:', error);
        setIsLoadingNewSamples(false);
        return;
      }
      setIsLoadingNewSamples(false);
    }

    // Start playback
    setIsPlaying(true);
    setTrackPositions(() => {
      const newPositions = {};
      sampleStore.selectedSamplesForSequencer.forEach((sample) => {
        const sampleId = sample.id.toString();
        newPositions[sampleId] = 0;
      });
      console.log('Reset trackPositions:', newPositions);
      return newPositions;
    });

    songModeRef.current = isSongMode;
    songSequenceRef.current = [...songSequence];
    currentSongStepRef.current = 0;

    // Initialize for the first pattern in song mode
    if (isSongMode && songSequence.length > 0) {
      setCurrentSongStep(0);
      // Use the function directly instead of reference
      if (patterns[songSequence[0]]) {
        const patternId = songSequence[0];
        setCurrentPattern(patternId);
        saveCurrentPattern(patternId);

        // Load pattern data
        const pattern = patterns[patternId];

        setSequenceData(pattern.sequenceData || {});
        setTrackLengths(pattern.trackLengths || {});
        setPlaybackRates(pattern.playbackRates || {});
        setVolumeLevels(pattern.volumeLevels || {});
        setMaxPlayDurations(pattern.maxPlayDurations || {});
      }
    }

    Tone.Transport.start();
  }, [
    isPlaying,
    isInitialized,
    playersLoaded,
    sampleStore.selectedSamplesForSequencer,
    initAudio,
    updatePlayers,
    stopAllPlayers,
    isSongMode,
    songSequence,
    patterns, // Add patterns dependency
  ]);

  // Modify togglePlayback to handle loading state
  const togglePlaybackWithLoading = async () => {
    if (!isInitialized || !playersLoaded) {
      setIsLoadingNewSamples(true);
      await togglePlayback();
      setIsLoadingNewSamples(false);
    } else {
      await togglePlayback();
    }
  };

  // Optionally disable the Play button until audio is ready
  const isAudioReady = isInitialized && playersLoaded;

  // Remove initAudio from useEffect
  useEffect(() => {
    // initAudio(); // Remove this call
    return () => {
      // Cleanup on unmount
      if (playersRef.current) {
        playersRef.current.dispose();
        playersRef.current = null;
      }
      if (sequencerRef.current) {
        sequencerRef.current.dispose();
        sequencerRef.current = null;
      }
      if (reverbRef.current) {
        reverbRef.current.dispose();
        reverbRef.current = null;
      }

      // Cancel all debounced functions
      saveSequenceData.cancel();
      savePlaybackRates.cancel();
      saveReverbMix.cancel();
      saveTrackLengths.cancel();
      saveVolumeLevels.cancel();
      saveMaxPlayDurations.cancel();
      saveTempo.cancel();
      saveHumanizationAmount.cancel();
      saveSwingAmount.cancel();
      saveSampleKeys.cancel();
      saveSequenceName.cancel();
      savePatterns.cancel();
      saveCurrentPattern.cancel();
      saveSongSequence.cancel();
    };
  }, []);

  // Remove updatePlayers from useEffect
  useEffect(() => {
    // if (isInitialized) {
    //   updatePlayers();
    // }
  }, [/* Remove dependencies */]);

  // Modify the sequence callback to include auto-scrolling
  const sequenceCallback = useCallback((time, step) => {
    if (!playersRef.current || !playersLoaded) return;

    const isSwung = step % 2 !== 0;
    let playbackTime = time;

    if (isSwung) {
      const swingOffset = swingAmount * Tone.Time('16n').toSeconds();
      playbackTime += swingOffset;
    }

    const humanizedTime =
      playbackTime - humanizationAmount / 2 + Math.random() * humanizationAmount;

    const calculatePlaybackRate = (originalTempo, currentTempo) => {
      if (!originalTempo) return 1;
      return currentTempo / originalTempo;
    };

    const stepDuration = 60 / tempo / 4;

    sampleStore.selectedSamplesForSequencer.slice(0, MAX_SAMPLES).forEach((sample) => {
      const sampleId = sample.id.toString();
      const trackLength = trackLengths[sampleId] || DEFAULT_TRACK_LENGTH;
      const trackPosition = step % trackLength;

      if (sequenceData[sampleId]?.[trackPosition]) {
        if (playersRef.current.has(sampleId)) {
          const player = playersRef.current.player(sampleId);
          if (player && player.buffer && player.buffer.loaded) {
            try {
              const originalTempo = sample.tempo;
              const tempoAdjustedRate = calculatePlaybackRate(originalTempo, tempo);
              const userAdjustedRate = playbackRates[sampleId] || 1;
              const keyAdjustedRate = sample.key && sampleKeys[sampleId]
                ? Math.pow(2, getSemitonesDifference(sample.key, sampleKeys[sampleId]) / 12)
                : 1;

              // Combine tempo adjustment with user-defined playback rate and key adjustment
              const combinedPlaybackRate = tempoAdjustedRate * userAdjustedRate * keyAdjustedRate;
              player.playbackRate = combinedPlaybackRate;

              player.volume.value = Tone.gainToDb(volumeLevels[sampleId] || 0.7);

              // Calculate the maximum duration based on track length
              const maxDurationByTrackLength = trackLength * stepDuration;

              const userDefinedMaxDuration = maxPlayDurations[sampleId] || player.buffer.duration;

              // Adjust durations based on playback rate
              const adjustedMaxDurationByTrackLength = (maxDurationByTrackLength * combinedPlaybackRate) - 0.05;
              const adjustedUserDefinedMaxDuration = userDefinedMaxDuration;
              const adjustedBufferDuration = player.buffer.duration;

              const adjustedDuration = Math.min(
                adjustedMaxDurationByTrackLength,
                adjustedUserDefinedMaxDuration,
                adjustedBufferDuration
              );

              // Add short fade-in/out to prevent clicks
              player.fadeIn = 0.005;
              player.fadeOut = 0.005;

              // Explicitly stop the player at the scheduled time before starting
              player.stop(humanizedTime);
              player.start(humanizedTime, 0, adjustedDuration);
            } catch (error) {
              console.error(`Error playing sample ${sampleId}:`, error);
              // Try to reload this sample on error
              const sample = sampleStore.selectedSamplesForSequencer.find(s => s.id.toString() === sampleId);
              if (sample) {
                try {
                  const url = new URL(sample.sample_mp3_url);
                  url.searchParams.append('t', Date.now());
                  playersRef.current.add(sampleId, url.toString());
                } catch (loadError) {
                  console.error(`Failed to reload sample ${sampleId}:`, loadError);
                }
              }
            }
          } else {
            console.warn(`Player buffer not loaded for sample slug: ${sampleId}`);
          }
        } else {
          console.warn(`Player not found for sample slug: ${sampleId}`);
        }
      }
    });

    // Add this to calculate the visual step based on the longest track
    const longestTrack = getLongestTrackLength();
    const visualStep = step % longestTrack;

    // Check if we need to switch patterns in song mode
    if (songModeRef.current && songSequenceRef.current.length > 0) {
      const currentPatternId = songSequenceRef.current[currentSongStepRef.current];
      const currentPatternData = patterns[currentPatternId];

      if (currentPatternData) {
        const maxLength = Object.values(currentPatternData.trackLengths || {}).reduce(
          (max, length) => Math.max(max, length), DEFAULT_TRACK_LENGTH
        );

        // If we've reached the end of the current pattern, move to the next pattern
        if ((step + 1) % maxLength === 0) {
          // Schedule the next pattern switch
          Tone.Draw.schedule(() => {
            currentSongStepRef.current = (currentSongStepRef.current + 1) % songSequenceRef.current.length;
            setCurrentSongStep(currentSongStepRef.current);
            const nextPatternId = songSequenceRef.current[currentSongStepRef.current];

            // Inline the pattern loading instead of calling loadPattern
            if (patterns[nextPatternId]) {
              setCurrentPattern(nextPatternId);
              saveCurrentPattern(nextPatternId);

              // Load pattern data
              const pattern = patterns[nextPatternId];

              setSequenceData(pattern.sequenceData || {});
              setTrackLengths(pattern.trackLengths || {});
              setPlaybackRates(pattern.playbackRates || {});
              setVolumeLevels(pattern.volumeLevels || {});
              setMaxPlayDurations(pattern.maxPlayDurations || {});
            }
          }, time + 0.01);
        }
      }
    }

    // Then call scrollToStep with the visual step
    Tone.Draw.schedule(() => {
      // Use visualStep instead of step for scrolling
      scrollToStep(visualStep);

      setTrackPositions((prevPositions) => {
        const newPositions = { ...prevPositions };
        sampleStore.selectedSamplesForSequencer.forEach((sample) => {
          const sampleId = sample.id.toString();
          const trackLength = trackLengths[sampleId] || DEFAULT_TRACK_LENGTH;
          newPositions[sampleId] = step % trackLength;
        });
        return newPositions;
      });
    }, time);
  }, [
    sequenceData,
    sampleStore.selectedSamplesForSequencer,
    trackLengths,
    playersLoaded,
    humanizationAmount,
    swingAmount,
    playbackRates,
    volumeLevels,
    maxPlayDurations,
    tempo,
    sampleKeys,
    scrollToStep,
    patterns,
    // Remove loadPattern from dependencies
  ]);

  // Recreate the sequence when sequenceCallback or playersLoaded changes
  useEffect(() => {
    if (!isInitialized || !playersLoaded) return;

    // Recreate the sequence with the updated callback
    if (sequencerRef.current) {
      sequencerRef.current.dispose();
    }

    sequencerRef.current = new Tone.Sequence(
      sequenceCallback,
      [...Array(MAX_STEPS).keys()],
      '16n'
    ).start(0);

    // Cleanup on unmount
    return () => {
      if (sequencerRef.current) {
        sequencerRef.current.dispose();
        sequencerRef.current = null;
      }
    };
  }, [
    sequenceCallback, // Ensure this effect runs when sequenceCallback changes
    isInitialized,
    playersLoaded,
  ]);

  // Handle sample control popover
  const handleSampleControlsPopoverOpen = (event, sampleId) => {
    setSampleControlsPopoverAnchor(event.currentTarget);
    setCurrentSampleId(sampleId);
  };

  const handleSampleControlsPopoverClose = () => {
    setSampleControlsPopoverAnchor(null);
    setCurrentSampleId(null);
  };

  // Handle clear pattern confirmation
  const confirmClearPattern = () => {
    setShowClearConfirmation(true);
  };

  const performClearPattern = () => {
    setShowClearConfirmation(false);
    clearPattern();
  };

  const cancelClearPattern = () => {
    setShowClearConfirmation(false);
  };

  // Update clearPattern function
  const clearPattern = () => {
    setSequenceData({});
    setPlaybackRates({});
    setVolumeLevels({});
    setMaxPlayDurations({});
    setTrackPositions({});
    setIsPlaying(false);

    // Reset track lengths to DEFAULT_TRACK_LENGTH for each sample
    const defaultLengths = {};
    sampleStore.selectedSamplesForSequencer.forEach(sample => {
      defaultLengths[sample.id.toString()] = DEFAULT_TRACK_LENGTH;
    });
    setTrackLengths(defaultLengths);

    // Update the current pattern in patterns with empty data
    const updatedPatterns = {
      ...patterns,
      [currentPattern]: {
        sequenceData: {},
        trackLengths: defaultLengths,
        playbackRates: {},
        volumeLevels: {},
        maxPlayDurations: {},
      }
    };
    setPatterns(updatedPatterns);
    localStorage.setItem('sequencerPatterns', JSON.stringify(updatedPatterns));

    snackbarStore.showSnackbar(`Pattern ${currentPattern} cleared`, "success");
  };

  // Helper function to calculate semitone difference
  const getSemitonesDifference = (originalKey, newKey) => {
    if (!originalKey || !newKey) return 0;
    const notes = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];
    const originalIndex = notes.indexOf(originalKey.replace(/\d+/, ''));
    const newIndex = notes.indexOf(newKey.replace(/\d+/, ''));
    if (originalIndex === -1 || newIndex === -1) return 0;
    return newIndex - originalIndex;
  };

  // Render sample controls
  const renderSampleControls = () => {
    const sampleId = currentSampleId;
    if (!sampleId) return null;

    const sample = sampleStore.selectedSamplesForSequencer.find(
      (s) => s.id.toString() === sampleId
    );

    const playbackRate = parseFloat(playbackRates[sampleId]) || 1;
    const volume = parseFloat(volumeLevels[sampleId]) || 0.7;
    const currentKey = sampleKeys[sampleId] || sample.key || 'C';

    const sampleDuration = sample && !isNaN(parseFloat(sample.duration))
      ? parseFloat(sample.duration)
      : 1.0;

    const maxPlayDuration = maxPlayDurations[sampleId] !== undefined
      ? maxPlayDurations[sampleId]
      : sampleDuration;

    const minDuration = 0.01;
    const maxDuration = sampleDuration;

    const minLog = Math.log10(minDuration);
    const maxLog = Math.log10(maxDuration);

    const sliderValue =
      ((Math.log10(maxPlayDuration) - minLog) / (maxLog - minLog)) * 100;

    return (
      <Box sx={{ width: 250, p: 2 }}>
        <Typography variant="h6" gutterBottom>
          Sample Controls
        </Typography>
        <Box sx={{ mb: 2 }}>
          <Typography gutterBottom>
            Pitch: {playbackRate.toFixed(2)}x
          </Typography>
          <Slider
            value={playbackRate}
            min={0.5}
            max={2}
            step={0.01}
            onChange={(_, newValue) => {
              setPlaybackRates(prev => {
                const updated = { ...prev, [sampleId]: newValue };
                savePlaybackRates(updated);
                return updated;
              });
            }}
            aria-labelledby="pitch-slider"
          />
        </Box>
        <Box sx={{ mb: 2 }}>
          <Typography gutterBottom>
            Key: {currentKey}
          </Typography>
          <Select
            value={currentKey}
            onChange={(e) => {
              const newKey = e.target.value;
              setSampleKeys(prev => {
                const updated = { ...prev, [sampleId]: newKey };
                saveSampleKeys(updated);
                return updated;
              });
              if (sample.key) {
                const semitonesDiff = getSemitonesDifference(sample.key, newKey);
                const newPlaybackRate = Math.pow(2, semitonesDiff / 12);
                setPlaybackRates(prev => {
                  const updated = { ...prev, [sampleId]: newPlaybackRate };
                  savePlaybackRates(updated);
                  return updated;
                });
              }
            }}
            fullWidth
          >
            {['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'].map(note => (
              <MenuItem key={note} value={note}>{note}</MenuItem>
            ))}
          </Select>
        </Box>
        <Box sx={{ mb: 2 }}>
          <Typography gutterBottom>
            Max Play Duration: {maxPlayDuration.toFixed(2)}s
          </Typography>
          <Slider
            value={sliderValue}
            min={0}
            max={100}
            step={0.1}
            onChange={(_, newValue) => {
              const logDuration = minLog + (newValue / 100) * (maxLog - minLog);
              const newDuration = Math.pow(10, logDuration);

              setMaxPlayDurations(prev => {
                const updated = { ...prev, [sampleId]: newDuration };
                saveMaxPlayDurations(updated);
                return updated;
              });
            }}
            aria-labelledby="max-play-duration-slider"
          />
        </Box>
      </Box>
    );
  };

  // Define the toggleCell function
  const toggleCell = async (sampleId, stepIndex) => {
    console.log('Toggling cell:', sampleId, stepIndex);

    // First, make sure Tone.js is initialized
    if (!isInitialized) {
      await initAudio();
    }

    // Check if this specific sample is loaded
    const sampleNeedsLoading = !playersRef.current || !playersRef.current.has(sampleId);

    // If players aren't loaded or this specific sample isn't loaded, initialize or update
    if (!playersLoaded || sampleNeedsLoading) {
      console.log(`Sample ${sampleId} needs loading, updating players...`);

      await updatePlayers();

      // Check specifically if this sample was loaded after the update
      if (!playersRef.current || !playersRef.current.has(sampleId)) {
        console.warn(`Still couldn't load sample ${sampleId} after update attempt`);
        snackbarStore.showSnackbar("Couldn't load sample. Will try again on play.", "warning");
      } else {
        console.log(`Successfully loaded sample ${sampleId}`);
      }
    }

    // Check again after initialization and proceed only if safe
    if (playersRef.current && playersRef.current.has(sampleId)) {
      setSequenceData((prev) => {
        const currentTrackData = prev[sampleId] || Array(MAX_STEPS).fill(false);
        const newTrackData = [...currentTrackData];
        newTrackData[stepIndex] = !newTrackData[stepIndex];

        const updatedSequenceData = {
          ...prev,
          [sampleId]: newTrackData,
        };
        saveSequenceData(updatedSequenceData);
        return updatedSequenceData;
      });
    } else {
      // Even if we couldn't get the sample loaded this time, still toggle the cell in the UI
      setSequenceData((prev) => {
        const currentTrackData = prev[sampleId] || Array(MAX_STEPS).fill(false);
        const newTrackData = [...currentTrackData];
        newTrackData[stepIndex] = !newTrackData[stepIndex];

        const updatedSequenceData = {
          ...prev,
          [sampleId]: newTrackData,
        };
        saveSequenceData(updatedSequenceData);
        return updatedSequenceData;
      });
    }
  };

  // Modify the renderSequencerGrid function
  const renderSequencerGrid = () => {
    const samples = sampleStore.selectedSamplesForSequencer.slice(0, MAX_SAMPLES);
    const ROW_HEIGHT = 45; // Increased row height for meter + slider

    // Calculate the maximum visible steps
    const maxTrackLength = Math.max(...Object.values(trackLengths), DEFAULT_TRACK_LENGTH);
    // Add 8 steps buffer, but don't exceed MAX_STEPS
    const visibleSteps = Math.min(Math.max(maxTrackLength, 32), MAX_STEPS);

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginLeft: '-70px' }}>
        <div style={{ display: 'flex', alignItems: 'stretch' }}>
          {/* Fixed left column for labels */}
          <div style={{ width: '135px', flexShrink: 0 }}>
            {/* Empty space for step numbers alignment */}
            <div style={{ height: 20 /* Adjust height if needed */ }}></div>
            {/* Sample Labels and Meters */}
            {samples.map((sample) => {
              const sampleId = sample.id.toString();
              // Log the value being passed to the meter component - REMOVED
              // console.log('Rendering meter for:', sampleId, 'Value:', meterValues[sampleId]);
              return (
                <div
                  key={sampleId}
                  style={{
                    height: ROW_HEIGHT,
                    display: 'flex',
                    flexDirection: 'column', // Stack name, meter, and slider vertically
                    justifyContent: 'center', // Center items vertically
                    alignItems: 'flex-end', // Align text to the right
                    paddingRight: '10px',
                  }}
                >
                  <div // Container for text elements
                    style={{
                      fontSize: '0.75rem',
                      lineHeight: '1.0',
                      cursor: 'pointer',
                      color: "#64B5F6",
                      marginBottom: '2px', // Space between text and meter
                      display: 'flex', // Keep key/mode inline
                      alignItems: 'center',
                      justifyContent: 'flex-end', // Align text right
                      width: '100%', // Ensure this div takes full available width
                    }}
                    onClick={(e) => handleSampleControlsPopoverOpen(e, sampleId)}
                   >
                     <span
                       style={{
                         overflow: 'hidden',
                         textOverflow: 'ellipsis',
                         whiteSpace: 'nowrap',
                         flexShrink: 1, // Allow this span to shrink
                         minWidth: 0,   // Necessary for text-overflow in a flex item
                       }}
                       title={titleize(sample.displayName)} // Show full name on hover
                     >
                       {titleize(sample.displayName)}
                     </span>
                     {sample.key && sample.mode && (
                       <span style={{ marginLeft: '5px', color: '#7f7f7f', flexShrink: 0 /* Prevent key/mode from shrinking */ }}>
                         <i className="fas fa-music" style={{ fontSize: '0.7rem', marginRight: '2px' }}></i>
                         {`${sample.key} ${sample.mode}`}
                       </span>
                     )}
                   </div>
                   {/* Add Volume Meter, passing the ref */}
                   <VolumeMeter
                     ref={el => volumeMeterDivsRef.current[sampleId] = el}
                   />
                   {/* Add Volume Slider directly below meter */}
                   <Slider
                     size="small"
                     value={parseFloat(volumeLevels[sampleId]) || 0.7}
                     min={0}
                     max={1}
                     step={0.01}
                     onChange={(_, newValue) => {
                       setVolumeLevels(prev => {
                         const updated = { ...prev, [sampleId]: newValue };
                         saveVolumeLevels(updated);
                         return updated;
                       });
                       const player = playersRef.current?.player(sampleId);
                       if (player) {
                         player.volume.value = Tone.gainToDb(newValue);
                       }
                     }}
                     sx={{
                       width: '100%', // Match meter width
                       height: '1px', // Make slider thin
                       mt: '4px', // Margin top for spacing
                       padding: '0 !important', // Override padding
                       '& .MuiSlider-thumb': {
                          width: 10, // Adjust size for triangle
                          height: 10, // Adjust size for triangle
                          backgroundColor: 'primary.main', // Ensure color
                          clipPath: 'polygon(50% 0%, 0% 100%, 100% 100%)', // Upward triangle
                          borderRadius: 0, // Remove border radius for sharp corners
                          marginTop: '-4px', // Adjust vertical position slightly
                       },
                       '& .MuiSlider-rail': {
                         height: 1, // Thin track
                       },
                       '& .MuiSlider-track': {
                         height: 1, // Thin track
                       },
                     }}
                     aria-labelledby={`volume-slider-${sampleId}`}
                   />
                 </div>
              );
            })}
          </div>

          {/* Single scroll container for steps and grid */}
          <GridContainer ref={gridContainerRef}>
            <div style={{
              width: `calc(${visibleSteps} * 24px)`,
              display: 'flex',
              flexDirection: 'column',
              pointerEvents: 'all',
            }}>
              {/* Step numbers */}
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: `repeat(${visibleSteps}, 20px)`,
                  gap: '4px',
                  height: 20, // Match height above labels
                  alignItems: 'center',
                }}
              >
                {Array.from({ length: visibleSteps }).map((_, stepIndex) => (
                  <div
                    key={stepIndex}
                    style={{
                      width: '20px',
                      textAlign: 'center',
                      fontSize: '0.6rem',
                      color: '#555',
                    }}
                  >
                    {(stepIndex + 1) % 4 === 0 ? stepIndex + 1 : ''}
                  </div>
                ))}
              </div>

              {/* Grid rows */}
              {samples.map((sample) => {
                const sampleId = sample.id.toString();
                return (
                  <div
                    key={sampleId}
                    style={{
                      display: 'grid',
                      gridTemplateColumns: `repeat(${visibleSteps}, 20px)`,
                      gap: '4px',
                      height: ROW_HEIGHT, // Use updated ROW_HEIGHT
                      alignItems: 'center',
                      pointerEvents: 'all',
                    }}
                  >
                    {Array.from({ length: visibleSteps }, (_, stepIndex) => {
                      const isActive = sequenceData[sampleId]?.[stepIndex] || false;
                      const isPlayingCell = isPlaying && trackPositions[sampleId] === stepIndex;
                      const trackLength = trackLengths[sampleId] || DEFAULT_TRACK_LENGTH;
                      const isWithinTrackLength = stepIndex < trackLength;

                      return (
                        <Cell
                          key={`${sampleId}-${stepIndex}`}
                          isActive={isActive}
                          isPlaying={isPlayingCell}
                          isDisabled={!playersRef.current || !playersRef.current.has(sampleId)}
                          isWithinTrackLength={isWithinTrackLength}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            if (isWithinTrackLength) {
                              toggleCell(sampleId, stepIndex);
                            }
                          }}
                        />
                      );
                    })}
                  </div>
                );
              })}
            </div>
          </GridContainer>

          {/* Fixed right column for controls */}
          <div style={{ flexShrink: 0 }}>
            {/* Empty space for step numbers alignment with "Steps" label */}
            <div style={{
              height: 20, // Match height above labels
              display: 'flex',
              alignItems: 'center',
              paddingLeft: '15px'
            }}>
              <Typography
                variant="caption"
                sx={{
                  color: '#757575',
                  fontSize: '0.75rem',
                  fontWeight: 500,
                }}
              >
                Steps
              </Typography>
            </div>
            {samples.map((sample) => {
              const sampleId = sample.id.toString();
              return (
                <div
                  key={sampleId}
                  style={{
                    height: ROW_HEIGHT, // Use updated ROW_HEIGHT
                    display: 'flex',
                    alignItems: 'center',
                    paddingLeft: '15px',
                  }}
                >
                  <TextField
                    type="number"
                    variant="standard"
                    value={trackLengths[sampleId] || DEFAULT_TRACK_LENGTH}
                    onChange={(e) => {
                      const newValue = Math.min(Math.max(1, parseInt(e.target.value) || 1), MAX_STEPS);
                      setTrackLengths(prevLengths => {
                        // Update local state first for responsiveness
                        const updated = { ...prevLengths, [sampleId]: newValue };
                        // Then trigger the debounced save with the latest state
                        saveTrackLengths(updated);
                        return updated;
                      });
                    }}
                    inputProps={{
                      min: 1,
                      max: MAX_STEPS,
                      style: { padding: '4px 8px' },
                      className: "browser-default"
                    }}
                    sx={{
                      width: '70px',
                      '& .MuiOutlinedInput-root': {
                        height: '32px',
                      }
                    }}
                    size="small"
                  />
                  <IconButton
                    size="small"
                    onClick={() => handleSampleDeselect(sample)}
                    sx={{ marginLeft: '15px' }}
                  >
                    <i className="fa fa-times"></i>
                  </IconButton>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  // Modify the tempo change handler
  const handleTempoChange = (e) => {
    const newTempo = Number(e.target.value);
    setTempo(newTempo);
    saveTempo(newTempo);
    Tone.Transport.bpm.value = newTempo;
  };

  // Modify the tempo blur handler
  const handleTempoBlur = () => {
    setTempo(prev => {
      const newTempo = Math.max(40, Math.min(240, prev || 120));
      saveTempo(newTempo);
      Tone.Transport.bpm.value = newTempo;
      return newTempo;
    });
  };

  // Update the recordingTimeoutRef declaration to a new recorder setup function
  const setupRecorder = useCallback(() => {
    if (!isInitialized) return;

    // Create a MediaRecorder connected to the destination
    const dest = Tone.Destination;
    const stream = dest.context.createMediaStreamDestination();
    dest.connect(stream);

    // Use a more compatible MIME type for all browsers
    const mimeTypes = [
      'audio/mp3',
      'audio/mpeg',
      'audio/webm',
      'audio/mp4'
    ];

    let mimeType = '';
    // Find the first supported MIME type
    for (const type of mimeTypes) {
      if (MediaRecorder.isTypeSupported(type)) {
        mimeType = type;
        console.log(`Using supported MIME type: ${mimeType}`);
        break;
      }
    }

    try {
      // Create recorder with appropriate options
      const options = mimeType ? { mimeType, audioBitsPerSecond: 128000 } : undefined;
      const mediaRecorder = new MediaRecorder(stream.stream, options);

      console.log(`Created MediaRecorder with mimeType: ${mediaRecorder.mimeType}`);

      mediaRecorder.ondataavailable = (evt) => {
        chunksRef.current.push(evt.data);
      };

      setRecorder(mediaRecorder);

      return () => {
        dest.disconnect(stream);
      };
    } catch (error) {
      console.error('Error creating MediaRecorder:', error);
      snackbarStore.showSnackbar('Recording is not supported in this browser', 'error');
      return () => {};
    }
  }, [isInitialized]);

  // Use the setup function in useEffect
  useEffect(() => {
    const cleanup = setupRecorder();
    return cleanup;
  }, [setupRecorder]);

  // Move getLongestTrackLength before getSongLengthInMeasures
  // Add this helper function near the top of the component
  const getLongestTrackLength = useCallback(() => {
    return Math.max(...Object.values(trackLengths), DEFAULT_TRACK_LENGTH);
  }, [trackLengths]);

  // Add a helper function to calculate the total song length in measures
  const getSongLengthInMeasures = useCallback(() => {
    if (!isSongMode || songSequence.length === 0) {
      // Default to single pattern length for Pattern Mode
      return Math.ceil(getLongestTrackLength() / 16);
    }

    // Calculate total measures for all patterns in the sequence
    let totalMeasures = 0;
    songSequence.forEach(patternId => {
      if (patterns[patternId]) {
        const patternData = patterns[patternId];
        const maxLength = Object.values(patternData.trackLengths || {}).reduce(
          (max, length) => Math.max(max, length), DEFAULT_TRACK_LENGTH
        );
        // Convert steps to measures (16 steps = 1 measure)
        totalMeasures += Math.ceil(maxLength / 16);
      }
    });

    return Math.max(totalMeasures, 1); // At least 1 measure
  }, [isSongMode, songSequence, patterns, getLongestTrackLength]);

  // Modify the startRecording function to handle song mode
  const startRecording = async () => {
    if (!recorder || isRecording) return;

    try {
      // Clear any existing chunks
      chunksRef.current = [];

      // Calculate exact recording duration in seconds based on beats
      let numMeasures;

      if (isSongMode && songSequence.length > 0) {
        // For song mode, calculate the total length of all patterns
        numMeasures = getSongLengthInMeasures();

        console.log('Recording setup (Song Mode):', {
          songSequence,
          totalMeasures: numMeasures,
          tempo,
          'Transport position': Tone.Transport.position,
          'Recorder MIME type': recorder.mimeType
        });

        // Make sure we're in song mode and at the beginning of the sequence
        if (!isPlaying) {
          // If not already playing, we need to ensure we start in song mode
          setIsSongMode(true);
          songModeRef.current = true;
          currentSongStepRef.current = 0;

          // Load the first pattern in the sequence
          if (patterns[songSequence[0]]) {
            const firstPatternId = songSequence[0];
            loadPattern(firstPatternId);
          }
        } else {
          // If already playing, stop first so we can restart from the beginning
          await togglePlayback();

          // Ensure we're in song mode
          setIsSongMode(true);
          songModeRef.current = true;
          currentSongStepRef.current = 0;

          // Load the first pattern in the sequence
          if (patterns[songSequence[0]]) {
            const firstPatternId = songSequence[0];
            loadPattern(firstPatternId);
          }
        }
      } else {
        // For pattern mode, just use the longest track in the current pattern
        const longestTrack = getLongestTrackLength();
        numMeasures = Math.ceil(longestTrack / 16);

        console.log('Recording setup (Pattern Mode):', {
          longestTrack,
          tempo,
          numMeasures,
          'Transport position': Tone.Transport.position,
          'Recorder MIME type': recorder.mimeType
        });
      }

      // Stop any ongoing playback
      if (isPlaying) {
        await togglePlayback();
      }

      // Reset the transport position to start of the loop
      Tone.Transport.position = 0;

      // Temporarily disable looping to avoid issues with scheduling
      const wasLooping = Tone.Transport.loop;
      Tone.Transport.loop = false;

      // Set initial phase
      setRecordingPhase("preparing");

      // Create a counter for tracking loops
      let loopCount = 0;

      // Use a callback for loop position tracking instead of scheduled events
      const loopCallback = () => {
        loopCount++;
        console.log(`Loop count: ${loopCount}, position: ${Tone.Transport.position}`);

        // First loop - prepare to record
        if (loopCount === 1) {
          console.log('First loop complete, starting recording on next loop');
          setRecordingPhase("preparing");
          snackbarStore.showSnackbar("Warming up... Recording will start on next loop", "info");
        }
        // Second loop - start recording
        else if (loopCount === 2) {
          console.log('Starting recording at loop position:', Tone.Transport.position);
          if (recorder.state !== 'recording') {
            recorder.start();
            setIsRecording(true);
            setRecordingPhase("recording");
            snackbarStore.showSnackbar(isSongMode ? "Recording entire song..." : "Recording in progress...", "info");
          }
        }
        // Third loop - stop recording
        else if (loopCount === 3) {
          console.log('Stopping recording at loop position:', Tone.Transport.position);
          if (recorder.state === 'recording') {
            recorder.stop();
            setIsRecording(false);
            setRecordingPhase("idle");
            snackbarStore.showSnackbar(isSongMode ? "Song recording completed!" : "Recording completed!", "success");
          }
          // Remove the callback after recording is complete
          Tone.Transport.clear(loopId);
          // Restore original loop setting
          Tone.Transport.loop = wasLooping;
        }
      };

      // For song mode, we need to ensure correct timing of the repeat
      // by using the total song length in measures
      const loopId = Tone.Transport.scheduleRepeat(
        loopCallback,
        `${numMeasures}:0:0`,  // Run every numMeasures measures
        '0:0:0'                // Start at the beginning
      );

      // Start playback
      await togglePlayback();

      // Return a cleanup function
      return () => {
        Tone.Transport.clear(loopId);
        Tone.Transport.loop = wasLooping;
        if (recorder.state === 'recording') {
          recorder.stop();
        }
        setRecordingPhase("idle");
      };
    } catch (error) {
      console.error('Error in startRecording:', error);
      setIsRecording(false);
      setRecordingPhase("idle");
    }
  };

  // Simplified stopRecording function
  const stopRecording = () => {
    if (!recorder || recorder.state !== 'recording') return;

    console.log('Manual stop recording at transport time:', Tone.Transport.position);
    recorder.stop();
    setIsRecording(false);
  };

  // Update the saveSequence function to include songSequence data when in song mode
  const saveSequence = async () => {
    if (!sequenceName.trim()) {
      alert('Please enter a sequence name before saving');
      return;
    }

    // Check if user is logged in before proceeding
    if (!userStore.currentUser) {
      snackbarStore.showSnackbar('You must be logged in to save sequences', 'error');
      return;
    }

    try {
      // Make sure audio is initialized before attempting to record
      if (!isInitialized) {
        console.log('Initializing audio before recording...');
        await initAudio();
        // Need to wait for recorder setup after initialization
        await new Promise(resolve => setTimeout(resolve, 500));
        setupRecorder();
      }

      // Make sure recorder is available
      if (!recorder) {
        console.log('Setting up recorder...');
        setupRecorder();
        // Give it a moment to initialize
        await new Promise(resolve => setTimeout(resolve, 500));

        // If still no recorder, show error and return
        if (!recorder) {
          alert('Unable to initialize audio recording. Please try clicking Play first, then Save.');
          return;
        }
      }

      // Start recording with precise timing
      const cleanup = await startRecording();

      // Create a promise that resolves when recording stops
      const recordingBlob = await new Promise((resolve, reject) => {
        // Save original onstop handler
        const originalOnStop = recorder.onstop;

        // Set up onstop handler
        recorder.onstop = () => {
          console.log('Recording stopped, cleaning up...');

          // Restore original handler
          recorder.onstop = originalOnStop;

          // Process the recorded data
          const blob = new Blob(chunksRef.current, { type: recorder.mimeType });
          chunksRef.current = [];

          // Force stop playback directly instead of using togglePlayback
          console.log('Stopping playback after recording');
          Tone.Transport.stop();
          stopAllPlayers();
          setIsPlaying(false);

          // Clean up the scheduled events
          if (cleanup) cleanup();

          // Resolve the promise with the blob after playback is stopped
          resolve(blob);
        };

        recorder.onerror = (event) => {
          if (cleanup) cleanup();
          reject(event.error);
        };
      });

      // Rest of the upload code remains the same
      // Get presigned URL from the server
      const presignResponse = await axios.get('/s3/presign');
      const { url, fields } = presignResponse.data;

      // Use an appropriate file extension based on the recording format
      let fileExtension = 'mp3'; // Default
      const mimeType = recorder.mimeType;

      if (mimeType.includes('webm')) {
        fileExtension = 'webm';
      } else if (mimeType.includes('mp4')) {
        fileExtension = 'mp4';
      } else if (mimeType.includes('mpeg') || mimeType.includes('mp3')) {
        fileExtension = 'mp3';
      }

      console.log(`Using file extension: ${fileExtension} for MIME type: ${mimeType}`);
      const filename = `${sequenceName}.${fileExtension}`;

      // Create FormData for the upload
      const formData = new FormData();
      Object.entries(fields).forEach(([key, value]) => {
        formData.append(key, value);
      });

      // Create file with appropriate extension
      const file = new File([recordingBlob], filename, { type: mimeType });

      // Append the file with the dynamic filename
      formData.append('file', file, filename);

      // Upload the recording to S3
      const uploadResponse = await axios.post(url, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      // Extract the uploaded file data from the response
      console.log('uploadResponse', uploadResponse);
      let id = fields.key;
      if (id.startsWith('cache/')) {
        id = id.substring('cache/'.length);
      }

      const uploadedFileData = {
        id: id, // The key is typically used as the file ID
        storage: 'cache',
        metadata: {
          size: recordingBlob.size,
          mime_type: mimeType,
          filename: filename, // Use the dynamic filename
        },
      };

      // Prepare data object with additional song mode information if applicable
      const sequenceDataToSave = {
        sequenceData,
        trackLengths,
        playbackRates,
        volumeLevels,
        maxPlayDurations,
        reverbMix,
        tempo,
        humanizationAmount,
        swingAmount,
        sampleKeys,
        // Add song mode data
        isSongMode,
        songSequence: isSongMode ? songSequence : undefined,
        patterns: isSongMode ? patterns : undefined,
      };

      // If we already have a sequence, update it
      if (currentSequenceRef.current) {
        currentSequenceRef.current.setName(sequenceName);
        currentSequenceRef.current.setData(sequenceDataToSave);
        currentSequenceRef.current.audio = uploadedFileData; // Attach the audio data
      } else {
        // Create a new sequence if we don't have one
        currentSequenceRef.current = new Sequence({
          name: sequenceName,
          data: sequenceDataToSave,
          audio: uploadedFileData, // Attach the audio data
        });
      }

      // Save the sequence with the audio URL
      await currentSequenceRef.current.save();

      // Show success message
      snackbarStore.showSnackbar(
        isSongMode
          ? `Song "${sequenceName}" saved successfully!`
          : `Sequence "${sequenceName}" saved successfully!`,
        "success"
      );

      // Redirect to the user's sequences page
      if (userStore.currentUser && userStore.currentUser.slug) {
        const redirectUrl = `/users/${userStore.currentUser.slug}/sequences`;
        console.log('Redirecting to:', redirectUrl);
        window.location.href = redirectUrl;
      } else {
        console.warn('Cannot redirect: User slug not available');
        snackbarStore.showSnackbar('Sequence saved, but could not redirect to your sequences page.', 'warning');
      }
    } catch (error) {
      console.error('Error saving sequence:', error);
      snackbarStore.showSnackbar(`Failed to save sequence: ${error.message}`, "error");
    }
  };

  // Add a useEffect to watch for changes in selectedSamplesForSequencer
  useEffect(() => {
    if (isInitialized && playersLoaded) {
      updatePlayers();
    }
  }, [sampleStore.selectedSamplesForSequencer, isInitialized]);

  // Modify the function that handles sample deselection
  const handleSampleDeselect = (sample) => {
    const sampleId = sample.id.toString();

    // Remove the sample from trackLengths
    setTrackLengths((prevLengths) => {
      const { [sampleId]: _, ...updatedLengths } = prevLengths;
      saveTrackLengths(updatedLengths);
      return updatedLengths;
    });

    // Remove the sample's data from sequenceData
    setSequenceData((prevData) => {
      const { [sampleId]: _, ...updatedData } = prevData;
      saveSequenceData(updatedData);
      return updatedData;
    });

    // Dispose the meter for the deselected sample
    const meter = metersRef.current[sampleId];
    if (meter) {
      try {
        meter.dispose();
        console.log(`Disposed meter for deselected sample ${sampleId}`);
      } catch(e) { console.error(`Error disposing meter for ${sampleId}:`, e); }
      delete metersRef.current[sampleId];
    }

    // Remove the player
    if (playersRef.current && playersRef.current.has(sampleId)) {
      try {
        // Get the player instance first
        const player = playersRef.current.player(sampleId);

        // If the player exists, disconnect it from any meters
        if (player && meter) {
          try {
            player.disconnect(meter);
          } catch(e) {
            console.error(`Error disconnecting player ${sampleId} from meter:`, e);
          }
        }

        // Delete from the players map - Tone.Players stores players in _players Map
        if (playersRef.current._players) {
          if (playersRef.current._players instanceof Map) {
            playersRef.current._players.delete(sampleId);
          } else if (typeof playersRef.current._players === 'object') {
            delete playersRef.current._players[sampleId];
          }
          console.log(`Removed player for deselected sample ${sampleId}`);
        }
      } catch(error) {
        console.error(`Error removing player ${sampleId}:`, error);
      }
    }

    // Remove the volume meter div ref
    if (volumeMeterDivsRef.current[sampleId]) {
      delete volumeMeterDivsRef.current[sampleId];
      console.log(`Removed volume meter div ref for ${sampleId}`);
    }

    // Deselect the sample in the store
    sampleStore.deselectSampleForSequencer(sample);
  };

  // Add this new function before the render
  const duplicatePatterns = () => {
    // Duplicate patterns and double track lengths
    setSequenceData((prevData) => {
      const newData = { ...prevData };  // Start with a copy of the existing data

      // Get all sample IDs from both sequence data and track lengths
      const allSampleIds = new Set([
        ...Object.keys(prevData),
        ...Object.keys(trackLengths),
        ...sampleStore.selectedSamplesForSequencer.map(s => s.id.toString())
      ]);

      // Process each sample ID
      allSampleIds.forEach((sampleId) => {
        const currentLength = trackLengths[sampleId] || DEFAULT_TRACK_LENGTH;
        const newLength = Math.min(currentLength * 2, MAX_STEPS);

        // Get existing pattern or create an empty one
        const existingPattern = prevData[sampleId] || Array(MAX_STEPS).fill(false);
        const newPattern = Array(MAX_STEPS).fill(false);

        // Copy original pattern
        for (let i = 0; i < currentLength; i++) {
          newPattern[i] = existingPattern[i];
        }

        // Add duplicated pattern
        for (let i = 0; i < currentLength; i++) {
          if (i + currentLength < MAX_STEPS) {
            newPattern[i + currentLength] = existingPattern[i];
          }
        }

        newData[sampleId] = newPattern;
      });

      // This will automatically update the pattern through saveSequenceData
      return newData;
    });

    // Double track lengths
    setTrackLengths((prevLengths) => {
      const newLengths = { ...prevLengths };

      // Get all sample IDs
      const allSampleIds = new Set([
        ...Object.keys(prevLengths),
        ...sampleStore.selectedSamplesForSequencer.map(s => s.id.toString())
      ]);

      // Double each track's length
      allSampleIds.forEach((sampleId) => {
        const currentLength = prevLengths[sampleId] || DEFAULT_TRACK_LENGTH;
        newLengths[sampleId] = Math.min(currentLength * 2, MAX_STEPS);
      });

      // This will automatically update the pattern through saveTrackLengths
      return newLengths;
    });

    snackbarStore.showSnackbar(`Pattern ${currentPattern} duplicated`, "success");
  };

  // Add this useEffect to watch for changes in selectedSamplesForSequencer during playback
  useEffect(() => {
    // Only dynamically update players if we're currently playing
    if (isPlaying && isInitialized) {
      console.log('Samples changed during playback, updating players...');
      updatePlayers();
    }
  }, [sampleStore.selectedSamplesForSequencer, isPlaying, isInitialized, updatePlayers]);

  // Add function to save current pattern state
  const saveCurrentPatternState = useCallback(() => {
    // The current pattern is already being saved with each individual update
    // to sequenceData, trackLengths, etc., so no need for additional saving
    console.log('Pattern state for', currentPattern, 'already being saved automatically');
  }, [currentPattern]);

  // Update loadPattern function to work with the new pattern-focused approach
  const loadPattern = useCallback((patternId) => {
    if (!patterns[patternId]) return;

    setCurrentPattern(patternId);
    saveCurrentPattern(patternId);

    // Load pattern data
    const pattern = patterns[patternId];

    setSequenceData(pattern.sequenceData || {});
    setTrackLengths(pattern.trackLengths || {});
    setPlaybackRates(pattern.playbackRates || {});
    setVolumeLevels(pattern.volumeLevels || {});
    setMaxPlayDurations(pattern.maxPlayDurations || {});

    // No need to save to localStorage here, as it will be handled by the update functions
    // when the state changes
  }, [patterns]);

  // Update createNewPattern function
  const createNewPattern = useCallback(() => {
    if (!newPatternName.trim()) {
      snackbarStore.showSnackbar("Please enter a pattern name", "warning");
      return;
    }

    // Create new pattern
    const updatedPatterns = {
      ...patterns,
      [newPatternName]: {
        sequenceData: {},
        trackLengths: {},
        playbackRates: {},
        volumeLevels: {},
        maxPlayDurations: {},
      }
    };

    // Update patterns state and localStorage
    setPatterns(updatedPatterns);
    localStorage.setItem('sequencerPatterns', JSON.stringify(updatedPatterns));

    // Switch to the new pattern
    setCurrentPattern(newPatternName);
    saveCurrentPattern(newPatternName);

    // Update local states to reflect the new pattern
    setSequenceData({});
    setTrackLengths({});
    setPlaybackRates({});
    setVolumeLevels({});
    setMaxPlayDurations({});

    // Clear the input field
    setNewPatternName('');

    snackbarStore.showSnackbar(`Pattern ${newPatternName} created`, "success");
  }, [newPatternName, patterns]);

  // Add handler for pattern deletion
  const deletePattern = useCallback((patternId) => {
    if (Object.keys(patterns).length <= 1) {
      snackbarStore.showSnackbar("Cannot delete the only pattern", "warning");
      return;
    }

    const updatedPatterns = { ...patterns };
    delete updatedPatterns[patternId];

    // Update song sequence to remove references to the deleted pattern
    const updatedSongSequence = songSequence.filter(id => id !== patternId);
    setSongSequence(updatedSongSequence);
    saveSongSequence(updatedSongSequence);

    // Update patterns state and localStorage
    setPatterns(updatedPatterns);
    localStorage.setItem('sequencerPatterns', JSON.stringify(updatedPatterns));

    // If we're deleting the current pattern, switch to another one
    if (currentPattern === patternId) {
      const newCurrentPattern = Object.keys(updatedPatterns)[0];
      setCurrentPattern(newCurrentPattern);
      saveCurrentPattern(newCurrentPattern);

      // Load the new pattern data
      const newPattern = updatedPatterns[newCurrentPattern];
      setSequenceData(newPattern.sequenceData || {});
      setTrackLengths(newPattern.trackLengths || {});
      setPlaybackRates(newPattern.playbackRates || {});
      setVolumeLevels(newPattern.volumeLevels || {});
      setMaxPlayDurations(newPattern.maxPlayDurations || {});
    }

    snackbarStore.showSnackbar(`Pattern ${patternId} deleted`, "success");
  }, [patterns, currentPattern, songSequence]);

  // Add handler for adding pattern to song sequence
  const addPatternToSequence = useCallback((patternId) => {
    setSongSequence(prevSequence => {
      const updatedSequence = [...prevSequence, patternId];
      saveSongSequence(updatedSequence);
      return updatedSequence;
    });
  }, []);

  // Add handler for removing pattern from song sequence
  const removePatternFromSequence = useCallback((index) => {
    setSongSequence(prevSequence => {
      const updatedSequence = [...prevSequence];
      updatedSequence.splice(index, 1);
      saveSongSequence(updatedSequence);
      return updatedSequence;
    });
  }, []);

  // Add handler for reordering song sequence
  const movePatternInSequence = useCallback((index, direction) => {
    setSongSequence(prevSequence => {
      const updatedSequence = [...prevSequence];

      if (direction === 'up' && index > 0) {
        const temp = updatedSequence[index];
        updatedSequence[index] = updatedSequence[index - 1];
        updatedSequence[index - 1] = temp;
      } else if (direction === 'down' && index < updatedSequence.length - 1) {
        const temp = updatedSequence[index];
        updatedSequence[index] = updatedSequence[index + 1];
        updatedSequence[index + 1] = temp;
      }

      saveSongSequence(updatedSequence);
      return updatedSequence;
    });
  }, []);

  // Add effect to watch for pattern changes
  useEffect(() => {
    // Save current pattern state when switching patterns
    return () => {
      saveCurrentPatternState();
    };
  }, [currentPattern, saveCurrentPatternState]);

  // Add handler for pattern duplication
  const duplicatePatternAsNew = useCallback((patternId) => {
    if (!patterns[patternId]) return;

    // Generate a new pattern name
    const existingNames = new Set(Object.keys(patterns));
    let newName = '';

    // Try letters first (A, B, C, etc.)
    for (let charCode = 65; charCode <= 90; charCode++) {
      const letter = String.fromCharCode(charCode);
      if (!existingNames.has(letter)) {
        newName = letter;
        break;
      }
    }

    // If all letters are taken, use numbers (Pattern 1, Pattern 2, etc.)
    if (!newName) {
      let counter = 1;
      while (!newName) {
        const testName = `Pattern ${counter}`;
        if (!existingNames.has(testName)) {
          newName = testName;
        }
        counter++;
      }
    }

    // Create new pattern as a copy of the selected one
    const sourcePattern = patterns[patternId];
    const newPattern = JSON.parse(JSON.stringify(sourcePattern)); // Deep copy

    const updatedPatterns = {
      ...patterns,
      [newName]: newPattern
    };

    // Update patterns state and localStorage
    setPatterns(updatedPatterns);
    localStorage.setItem('sequencerPatterns', JSON.stringify(updatedPatterns));

    snackbarStore.showSnackbar(`Pattern ${patternId} duplicated as ${newName}`, "success");
  }, [patterns]);

  // Modify the pattern manager dialog to avoid loadPattern reference
  const renderPatternManager = () => {
    return (
      <Dialog
        open={showPatternManager}
        onClose={() => setShowPatternManager(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Pattern Manager</DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom>Create New Pattern</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <TextField
                value={newPatternName}
                onChange={(e) => setNewPatternName(e.target.value)}
                label="Pattern Name"
                variant="outlined"
                size="small"
                sx={{ width: '200px' }}
                inputProps={{
                  className: 'browser-default',
                }}
              />
              <Button
                variant="contained"
                onClick={createNewPattern}
                disabled={!newPatternName.trim()}
              >
                Create Pattern
              </Button>
            </Box>
          </Box>

          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom>Available Patterns</Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {Object.keys(patterns).map(patternId => (
                <Box
                  key={patternId}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    border: '1px solid',
                    borderColor: currentPattern === patternId ? 'primary.main' : 'grey.300',
                    borderRadius: 1,
                    p: 1,
                    backgroundColor: 'background.paper',
                  }}
                >
                  <Typography>{patternId}</Typography>
                  <IconButton
                    size="small"
                    onClick={() => {
                      // No need to call saveCurrentPatternState since state is saved automatically
                      loadPattern(patternId);
                      setShowPatternManager(false);
                    }}
                    title="Select pattern"
                  >
                    <i className="fa fa-check"></i>
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => addPatternToSequence(patternId)}
                    title="Add to song sequence"
                  >
                    <i className="fa fa-plus"></i>
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => duplicatePatternAsNew(patternId)}
                    title="Duplicate pattern as new"
                  >
                    <i className="fa fa-clone"></i>
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => deletePattern(patternId)}
                    title="Delete pattern"
                    disabled={Object.keys(patterns).length <= 1}
                  >
                    <i className="fa fa-trash"></i>
                  </IconButton>
                </Box>
              ))}
            </Box>
          </Box>

          {/* The rest of the dialog content */}
          <Box>
            <Typography variant="h6" gutterBottom>Song Sequence</Typography>
            {songSequence.length === 0 ? (
              <Typography color="text.secondary">
                No patterns in sequence. Add patterns from above.
              </Typography>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {songSequence.map((patternId, index) => (
                  <Box
                    key={`sequence-${index}`}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      border: '1px solid',
                      borderColor: 'grey.300',
                      borderRadius: 1,
                      p: 1,
                      backgroundColor: isSongMode && currentSongStep === index ? 'success.light' : 'background.paper',
                    }}
                  >
                    <Typography sx={{ minWidth: '30px' }}>{index + 1}.</Typography>
                    <Typography sx={{ flex: 1 }}>{patternId}</Typography>
                    <IconButton
                      size="small"
                      onClick={() => movePatternInSequence(index, 'up')}
                      disabled={index === 0}
                    >
                      <i className="fa fa-arrow-up"></i>
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => movePatternInSequence(index, 'down')}
                      disabled={index === songSequence.length - 1}
                    >
                      <i className="fa fa-arrow-down"></i>
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => removePatternFromSequence(index)}
                    >
                      <i className="fa fa-times"></i>
                    </IconButton>
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowPatternManager(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    );
  };

  // Simple Volume Meter Component - Modified to accept a ref
  const VolumeMeter = React.forwardRef(({ value }, ref) => {
    // Log the received value - No longer needed as value prop is removed
    // console.log('VolumeMeter props value:', value);
    const meterHeight = '5px'; // Height of the meter bar
    const meterWidth = '100%'; // Width relative to container
    const maxLevel = 1.0; // Max gain value

    // Initial percentage calculation (might be 0 initially)
    const initialClampedValue = Math.min(Math.max(value || 0, 0), maxLevel);
    const initialPercentage = (initialClampedValue / maxLevel) * 100;

    // Simple gradient for visual feedback
    const backgroundColor = `linear-gradient(to right, #4caf50 ${initialPercentage}%, #e0e0e0 ${initialPercentage}%)`;

    return (
      <div style={{
        width: meterWidth,
        height: meterHeight,
        backgroundColor: '#e0e0e0', // Background of the meter track
        borderRadius: '2px',
        overflow: 'hidden', // Hide overflow
        marginTop: '2px', // Space below sample name
      }}>
        {/* Attach the forwarded ref to this inner div */}
        <div ref={ref} style={{
          width: `${initialPercentage}%`, // Set initial width
          height: '100%',
          backgroundColor: '#4caf50', // Color of the meter level
          // Remove transition, direct style updates are fast enough
          // transition: 'width 0.05s linear',
        }} />
      </div>
    );
  });

  // Effect to read meter values - Modified to update style directly
  useEffect(() => {
    let animationFrameId;

    const updateMeters = () => {
      // No need to check isInitialized/playersLoaded here,
      // if the meter/div refs exist, Tone.js is likely ready
      // and it's safe to call getValue()

      Object.entries(metersRef.current).forEach(([sampleId, meter]) => {
        const meterDiv = volumeMeterDivsRef.current[sampleId];

        // Check if meter and corresponding div element exist
        if (meter && typeof meter.getValue === 'function' && meterDiv) {
            const dBValue = meter.getValue();
            let percentage = 0;
            if (typeof dBValue === 'number' && isFinite(dBValue)) {
                const clampedDb = Math.max(-60, dBValue); // Use -60dB as practical silence for visualization
                const gainValue = Tone.dbToGain(clampedDb);
                percentage = Math.min(100, Math.max(0, gainValue * 100)); // Ensure percentage is 0-100
            } else {
                // Handle invalid dB value if necessary, default is 0%
            }
            // Directly update the width style
            meterDiv.style.width = `${percentage}%`;
        } else {
          // Optional: Warn if meter exists but div doesn't, or vice versa
          // if (meter && !meterDiv) console.warn(`Meter exists for ${sampleId} but no div ref`);
        }
      });

      animationFrameId = requestAnimationFrame(updateMeters);
    };

    animationFrameId = requestAnimationFrame(updateMeters);

    return () => {
      cancelAnimationFrame(animationFrameId);
    };
    // No dependencies needed now, runs continuously
  }, []);
  // Render component
  return (
    // Main container using flex column layout
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' /* Assuming parent provides height context */ }}>

      {/* Sequencer Grid Area (takes remaining space) */}
      <Box sx={{ flexGrow: 1, p: 1 /* Add some padding around grid */ }}>
        {renderSequencerGrid()}
      </Box>

      {/* Bottom Control Bar */}
      <Box sx={{ p: 1 }}>
        {/* Main Responsive Stack for Control Groups */}
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={{ xs: 2, sm: 1 }} // Adjust spacing as needed
          alignItems={{ xs: 'stretch', sm: 'center' }}
          justifyContent="space-between"
          sx={{ flexWrap: 'wrap' }} // Allow groups to wrap on smaller screens if needed
        >
          {/* --- Sequence / Pattern Management Group --- */}
          <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap' }}>
            <TextField
              value={sequenceName}
              onChange={(e) => {
                const newName = e.target.value;
                setSequenceName(newName);
                saveSequenceName(newName);
              }}
              label="Sequence Name"
              variant="outlined"
              size="small"
              inputProps={{
                className: "browser-default"
              }}
              sx={{
                // mr: 2, // Removed, handled by Stack spacing
                width: '180px', // Slightly smaller width?
                '& .MuiOutlinedInput-root': {
                  height: '36.5px',
                  '&:not(.Mui-focused) fieldset': {
                    borderColor: 'rgba(0, 0, 0, 0.23)',
                  },
                }
              }}
            />
            <Button
              variant="outlined"
              onClick={() => {
                saveCurrentPatternState();
                setShowPatternManager(true);
              }}
              startIcon={<i className="fa-solid fa-list"></i>}
              sx={{
                // mr: 2, // Removed
                '&:focus, &.Mui-focusVisible': {
                  backgroundColor: 'white !important',
                },
              }}
            >
              Patterns
            </Button>
            <FormControlLabel
              control={
                <StyledSwitch
                  checked={isSongMode}
                  onChange={() => {
                    setIsSongMode(!isSongMode);
                    songModeRef.current = !isSongMode;
                  }}
                  disabled={songSequence.length === 0}
                  color="success"
                />
              }
              label="Song Mode"
              // sx={{ mr: 2 }} // Removed
              disabled={songSequence.length === 0}
            />
            <Box sx={{ display: 'flex', alignItems: 'center' /* , mr: 2 Removed */ }}>
              <Typography variant="body2" sx={{ mr: 1 }}>
                {isSongMode ? "Seq:" : "Pat:"} {/* Shorter label */}
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  fontWeight: 'bold',
                  color: isSongMode ? 'success.main' : 'primary.main'
                }}
              >
                {isSongMode
                  ? `${currentSongStep + 1}/${songSequence.length} (${songSequence[currentSongStep] || '?'})`
                  : currentPattern}
              </Typography>
            </Box>
          </Stack>

          {/* --- Playback Group --- */}
          <Stack direction="row" spacing={1} alignItems="center">
            <SFPlayButton
              variant="contained"
              onClick={togglePlaybackWithLoading}
              startIcon={
                <i className={isPlaying ? 'fa-solid fa-stop' : 'fa-solid fa-play'}></i>
              }
              color={isPlaying ? 'alert' : 'playing'}
              // sx={{ mr: 2 }} // Removed
              isPlaying={isPlaying}
              disabled={isLoadingNewSamples}
            >
              {isPlaying ? 'Stop' : isLoadingNewSamples ? 'Loading...' : 'Play'}
            </SFPlayButton>
            {isLoadingNewSamples && (
              <Typography variant="body2" color="textSecondary" /* sx={{ mr: 2 }} Removed */>
                Loading...
              </Typography>
            )}
            <TextField
              id="tempo-input"
              type="number"
              value={tempo}
              label="Tempo"
              onChange={handleTempoChange}
              onBlur={handleTempoBlur}
              inputProps={{
                min: 40,
                max: 240,
                step: 1,
                className: "browser-default",
              }}
              sx={{ width: '80px' }}
              size="small"
              variant="outlined"
            />
          </Stack>

          {/* --- Pattern Actions / Settings Group --- */}
          {/* Combine these for potentially better mobile layout */}
          <Stack direction="row" spacing={1} alignItems="center">
            <SFClearButton
              variant="outlined"
              onClick={confirmClearPattern}
              startIcon={<i className="fa-solid fa-trash"></i>}
              color="alert"
              // sx={{ mr: 2 }} // Removed
            >
              Clear
            </SFClearButton>
            <Tooltip title="Duplicate patterns and double track lengths">
              <SFIconButton
                onClick={duplicatePatterns}
                color="primary"
                // sx={{ mr: 2 }} // Removed
              >
                <i className="fa-solid fa-clone"></i>
              </SFIconButton>
            </Tooltip>
            <Tooltip title="Global Controls (Reverb, Humanization, Swing)">
              <SFIconButton onClick={(e) => setReverbPopoverAnchor(e.currentTarget)} /* sx={{ mr: 2 }} Removed */>
                <i className="fa-solid fa-sliders"></i>
              </SFIconButton>
            </Tooltip>
          </Stack>

          {/* --- Save Action Group --- */}
          <Box sx={{ /* Use Box to help with potential justify issues if needed */ }}>
            <SFSaveButton
              variant="contained"
              onClick={saveSequence}
              startIcon={isRecording || recordingPhase !== "idle" ?
                <i className="fa-solid fa-circle-notch fa-spin"></i> :
                <i className="fa-solid fa-save"></i>
              }
              color={isRecording || recordingPhase !== "idle" ? "alert" : "primary"}
              disabled={isRecording || recordingPhase !== "idle"}
            >
              {recordingPhase === "preparing" ? "Preparing..." :
               recordingPhase === "recording" ? "Recording..." : "Save"}
            </SFSaveButton>
          </Box>
        </Stack>
      </Box>

      {/* Popovers and Dialogs should remain outside the main layout flow */}
      <Popover
        open={Boolean(sampleControlsPopoverAnchor)}
        anchorEl={sampleControlsPopoverAnchor}
        onClose={handleSampleControlsPopoverClose}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
      >
        {renderSampleControls()}
      </Popover>
      <Popover
        open={Boolean(reverbPopoverAnchor)}
        anchorEl={reverbPopoverAnchor}
        onClose={() => setReverbPopoverAnchor(null)}
        anchorOrigin={{
          vertical: 'top', // Adjust if opening from bottom bar
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
      >
         <Box sx={{ width: 250, p: 2 }}>
            {/* ... Popover content ... */}
            <Typography variant="h6" gutterBottom>
              Global Controls
            </Typography>
            {/* Reverb Mix */}
            <Box sx={{ mb: 2 }}>
              <Typography gutterBottom>
                Reverb Mix: {Math.round(reverbMix * 100)}%
              </Typography>
              <Slider
                value={reverbMix}
                min={0}
                max={1}
                step={0.01}
                onChange={(_, newValue) => {
                  setReverbMix(newValue);
                  saveReverbMix(newValue);
                  if (reverbRef.current) {
                    reverbRef.current.wet.value = newValue;
                  }
                }}
                aria-labelledby="reverb-mix-slider"
              />
            </Box>
            {/* Humanization Control */}
            <Box sx={{ mb: 2 }}>
              <Typography gutterBottom>
                Humanization: {(humanizationAmount * 1000).toFixed(0)} ms
              </Typography>
              <Slider
                value={humanizationAmount}
                min={0}
                max={0.1}
                step={0.001}
                onChange={(_, newValue) => {
                  setHumanizationAmount(newValue);
                  saveHumanizationAmount(newValue);
                }}
                aria-labelledby="humanization-slider"
              />
            </Box>
            {/* Swing Control */}
            <Box sx={{ mb: 2 }}>
              <Typography gutterBottom>
                Swing: {Math.round(swingAmount * 100)}%
              </Typography>
              <Slider
                value={swingAmount}
                min={0}
                max={1}
                step={0.01}
                onChange={(_, newValue) => {
                  setSwingAmount(newValue);
                  saveSwingAmount(newValue);
                }}
                aria-labelledby="swing-slider"
              />
            </Box>
          </Box>
      </Popover>
      <Dialog
        open={showClearConfirmation}
        onClose={cancelClearPattern}
      >
        <DialogTitle>{"Clear Pattern"}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to clear the pattern? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={cancelClearPattern} color="primary">
            Cancel
          </Button>
          <Button onClick={performClearPattern} variant="contained" color="alert">
            Clear
          </Button>
        </DialogActions>
      </Dialog>
      {renderPatternManager()}
    </Box> // Close main container Box
  );
};

// Export the component after wrapping with inject and observer
export default inject("sampleStore", "userStore", "snackbarStore")(observer(Sequencer));